// modules/chariot/ui/src/sections/insights/queryBuilder/utils/graphIcons.ts
import {
  createAttributeIconDataURL,
  createCloudIconDataURL,
  createComputerIconDataURL,
  createDomainIconDataURL,
  createGroupIconDataURL,
  createTechnologyIconDataURL,
  createUserIconDataURL,
} from '@/components/icons/nodeIcons';

/**
 * Icon cache to avoid regenerating the same icons multiple times
 */
const iconCache = new Map<string, string>();

/**
 * Get icon data URL for a given entity type with fallback to default
 * Returns both the icon and a flag indicating if the default was used
 *
 * @param type - Entity type (e.g., 'ADComputer', 'ADUser', 'ADGroup')
 * @param theme - Current theme ('light' or 'dark')
 * @returns Object with icon data URL and isDefault flag
 */
export function getIconOrDefault(
  type: string | undefined,
  theme: 'light' | 'dark'
): { icon: string; isDefault: boolean } {
  const recognizedIcon = getIconForType(type, theme);

  if (recognizedIcon) {
    return { icon: recognizedIcon, isDefault: false };
  }

  // Fallback to default attribute icon
  return { icon: createAttributeIconDataURL(theme, false), isDefault: true };
}

/**
 * Clear the icon cache (useful for theme changes or testing)
 */
export function clearIconCache(): void {
  iconCache.clear();
}

/**
 * Get icon data URL for a given entity type
 * Supports AD object types with themed icons and caching for performance
 *
 * @param type - Entity type (e.g., 'ADComputer', 'ADUser', 'ADGroup')
 * @param theme - Current theme ('light' or 'dark')
 * @returns Data URL for the icon or undefined if type is not recognized
 */
export function getIconForType(
  type: string | undefined,
  theme: 'light' | 'dark'
): string | undefined {
  if (!type) return undefined;

  type = type.toLowerCase();

  // Create cache key with theme to ensure correct icon for current theme
  const cacheKey = `${type}-${theme}`;

  if (iconCache.has(cacheKey)) {
    return iconCache.get(cacheKey);
  }

  let icon: string | undefined;

  switch (type.toLowerCase()) {
    case 'adcomputer':
    case 'asset':
      icon = createComputerIconDataURL(theme, false);
      break;
    case 'adgroup':
      icon = createGroupIconDataURL(theme, false);
      break;
    case 'aduser':
      icon = createUserIconDataURL(theme, false);
      break;
    case 'addomain':
      icon = createDomainIconDataURL(theme, false);
      break;
    case 'awsresource':
    case 'gcpresource':
    case 'azureresource':
      icon = createCloudIconDataURL(theme, false);
      break;
    case 'technology':
      icon = createTechnologyIconDataURL(theme, false);
      break;
    default:
      // Return undefined for unrecognized types
      return undefined;
  }

  if (icon) {
    iconCache.set(cacheKey, icon);
  }

  return icon;
}
